import React from "react";
import {
  ClockCircleOutlined,
  CalendarOutlined,
  ScheduleOutlined,
  HomeOutlined,
  DollarOutlined,
  CheckCircleOutlined,
  SolutionOutlined,
  ApartmentOutlined,
  GiftOutlined,
  BulbOutlined,
} from "@ant-design/icons";
import { getResource } from "../../util/getResourcePermission";
import { getRoles } from "../../util/getRoles";
import config from "../../permission/config.json";

// Section 8 of the approved Global Search spec - "Static HRMS Query Prompts,
// No AI". Each entry is a canned one-click action: a label + a destination
// route + (optionally) REAL route query params the destination page already
// reads off window.location.search - NOT operator-chip syntax (operator
// chips in searchOperators.js only affect Global Search's own result
// dropdown, never an arbitrary destination page).
//
// Every {route, resource, roles} triple below is copied VERBATIM from one of
// the three already-verified-safe sources this session: QUICK_LINKS
// (./index.js), ROLE_SUGGESTED_SEARCHES (./roleSuggestedSearches.js), or
// MODULE_SEARCH_INDEX (./moduleSearchIndex.js). Nothing here invents a new
// route, resource, or role gate.
//
// Param-read claims verified against the real destination component this
// session (file + what was checked):
// - /admin-leave-approval: AdminLeaveApprovel.js reads `employee_id` and
//   `leave_status` straight off `new URLSearchParams(window.location.search)`
//   (around its own L116-135) and auto-applies them on load - so
//   `?leave_status=Pending` below is a REAL, page-honoured filter.
// - /attendence-reports/attendence-register (admin): AttendenceRegister.js
//   only reads `employee_id` off the URL (L53-62) - no date/today/range or
//   status param exists there, so "Show my team attendance today" is a
//   PLAIN link, not a fabricated `?date=today`.
// - /user-reports/attendence-reports/attendence-register:
//   UserAttendenceRegister.js reads no query params at all - its date range
//   is plain `useState` seeded to the current month, nothing is read off
//   `window.location`. "Show my attendance today" is a PLAIN link.
// - /leave-application: LeaveApplication.js reads no query params either -
//   its status/date filters only come from the in-page "Filter" modal's
//   antd Form state (`filterData`), never the URL. "Show my pending leave
//   requests" is a PLAIN link.
// - /employee-attendence-regularization, /work-from-home-application,
//   /bill-reimbursement, /my-approvals, /onboarding-initiation, /admin-shift,
//   /admin-department, /holidays: none of these were found (in
//   moduleSearchIndex.js/roleSuggestedSearches.js, nor by the param checks
//   above) to read any query param worth pre-applying - all PLAIN links.
//
// Prompts intentionally NOT built (see GOAL spec for the reasoning on each):
// - "What is my leave balance?" for Employee - Leave Balance has no safe
//   self-view (QUICK_LINKS' own audit comment), so it's offered to
//   Manager/HR/Admin only below (as "Team/My Leave Balance" equivalents
//   already exist in Suggested for You - not duplicated here), never Employee.
// - "Show upcoming company holidays" for Employee - Holidays is Admin/HR
//   only (the only /holidays route IS the admin CRUD page, per QUICK_LINKS).
// - "Show employees on leave today" / "absent today" (HR) - no destination
//   page reads a date="today" + leave/absent-status combination; faking one
//   would silently filter nothing. Skipped, not faked.
// - "Show new joiners this month" (HR) - no verified destination/param.
// - "Show active approval workflows" (Admin) - no distinct verified route
//   beyond /my-approvals (already offered to the Manager bucket).

const ADMIN_SCOPE_ROLES = ["Admin", "HR", "Reporting Manager", "Assistance Reporting Manager"];

export const SEARCH_PROMPTS = {
  Employee: [
    {
      id: "prompt-my-attendance-today",
      label: "Show my attendance today",
      route: "/user-reports/attendence-reports/attendence-register",
      params: null,
      resource: config.RESOURCES.ATTENDANCE_REPORT,
      icon: <ClockCircleOutlined />,
      color: "#0891B2",
    },
    {
      id: "prompt-my-pending-leave",
      label: "Show my pending leave requests",
      route: "/leave-application",
      params: null,
      resource: config.RESOURCES.LEAVE,
      icon: <CalendarOutlined />,
      color: "#059669",
    },
    {
      id: "prompt-my-regularization",
      label: "Show my pending regularization requests",
      route: "/employee-attendence-regularization",
      params: null,
      resource: config.RESOURCES.ATTENDANCE_REGULARIZATION,
      icon: <ScheduleOutlined />,
      color: "#0E7490",
    },
    {
      id: "prompt-my-wfh",
      label: "Show my WFH requests",
      route: "/work-from-home-application",
      params: null,
      resource: config.RESOURCES.LEAVE,
      icon: <HomeOutlined />,
      color: "#059669",
    },
    {
      id: "prompt-my-reimbursements",
      label: "Show my pending reimbursements",
      route: "/bill-reimbursement",
      params: null,
      resource: config.RESOURCES.BILLREIMBURSEMENT,
      icon: <DollarOutlined />,
      color: "#CA8A04",
    },
  ],

  Manager: [
    {
      id: "prompt-team-leave-pending",
      label: "Show pending team leave requests",
      route: "/admin-leave-approval",
      params: { leave_status: "Pending" },
      resource: config.RESOURCES.ADMIN_LEAVE_MANAGEMENT,
      icon: <CalendarOutlined />,
      color: "#16A34A",
    },
    {
      id: "prompt-team-attendance-today",
      label: "Show my team attendance today",
      route: "/attendence-reports/attendence-register",
      params: null,
      resource: config.RESOURCES.ATTENDANCE_REPORT,
      roles: ADMIN_SCOPE_ROLES,
      icon: <ClockCircleOutlined />,
      color: "#2563EB",
    },
    {
      id: "prompt-my-approvals",
      label: "Show pending approvals assigned to me",
      route: "/my-approvals",
      params: null,
      resource: config.RESOURCES.APPROVAL_INBOX,
      icon: <CheckCircleOutlined />,
      color: "#0D9488",
    },
  ],

  HR: [
    {
      id: "prompt-leave-approvals-pending",
      label: "Show pending leave approvals",
      route: "/admin-leave-approval",
      params: { leave_status: "Pending" },
      resource: config.RESOURCES.ADMIN_LEAVE_MANAGEMENT,
      icon: <CalendarOutlined />,
      color: "#16A34A",
    },
    {
      id: "prompt-pending-onboarding",
      label: "Show pending onboarding tasks",
      route: "/onboarding-initiation",
      params: null,
      resource: config.RESOURCES.ON_BOARDING_INITIATION,
      roles: ["Admin", "HR"],
      icon: <SolutionOutlined />,
      color: "#7C3AED",
    },
  ],

  Admin: [
    {
      id: "prompt-configured-shifts",
      label: "Show configured shifts",
      route: "/admin-shift",
      params: null,
      resource: config.RESOURCES.SHIFT,
      roles: ["Admin", "HR"],
      icon: <ScheduleOutlined />,
      color: "#D97706",
    },
    {
      id: "prompt-departments",
      label: "Show departments",
      route: "/admin-department",
      params: null,
      resource: config.RESOURCES.DEPARTMENTS,
      roles: ["Admin", "HR"],
      icon: <ApartmentOutlined />,
      color: "#0E7490",
    },
    {
      id: "prompt-upcoming-holidays",
      label: "Show upcoming holidays",
      route: "/holidays",
      params: null,
      resource: config.RESOURCES.HOLIDAYS,
      roles: ["Admin", "HR"],
      icon: <GiftOutlined />,
      color: "#DB2777",
    },
    // "Try asking" examples for the Admin diagnostic FAQ feature (see
    // server/controller/diagnosticSearch.js / useGlobalSearch's
    // diagnosticAnswer). These don't navigate anywhere - `fillOnly: true` +
    // `template` tell index.js's handleSelectPrompt to fill the search input
    // with the template (selecting the "[employee name]" placeholder so
    // typing immediately replaces it) and keep the dropdown open, instead of
    // the normal navigate-and-close behavior every other prompt above uses.
    // No route/resource/roles needed - Admin-only is already enforced by
    // these living in the Admin bucket, same as every other entry here.
    {
      id: "prompt-diag-why-absent",
      label: "Why is someone absent?",
      fillOnly: true,
      template: "why is [employee name] absent",
      icon: <BulbOutlined />,
      color: "#0EA5E9",
    },
    {
      id: "prompt-diag-why-halfday",
      label: "Why is someone still half-day?",
      fillOnly: true,
      template: "why is [employee name] still half day",
      icon: <BulbOutlined />,
      color: "#0EA5E9",
    },
    {
      id: "prompt-diag-why-penalty",
      label: "Why did someone get a penalty?",
      fillOnly: true,
      template: "why did [employee name] get a penalty",
      icon: <BulbOutlined />,
      color: "#0EA5E9",
    },
  ],
};

// Same bucket-detection ladder as getSuggestedSearchesForViewer() in
// roleSuggestedSearches.js (most-privileged match wins - not a merge across
// buckets), then the identical resource+roles permission filter, capped at 6
// per the spec ("maximum 4-6 initially").
export const getSuggestedPromptsForViewer = () => {
  let bucket;
  if (getRoles(["Admin"])) bucket = "Admin";
  else if (getRoles(["HR"])) bucket = "HR";
  else if (getRoles(["Reporting Manager", "Assistance Reporting Manager"])) bucket = "Manager";
  else bucket = "Employee";

  const list = SEARCH_PROMPTS[bucket] || [];
  return list
    .filter(
      (entry) => (!entry.resource || getResource(entry.resource, ["read"])) && (!entry.roles || getRoles(entry.roles))
    )
    .slice(0, 6);
};
